/**
 * "ResizeObserver loop completed with undelivered notifications." is not an error.
 *
 * It is a notice defined by the Resize Observer spec: when observer callbacks change layout,
 * the browser runs them again on the next frame, and if observations are still pending after
 * the depth limit it reports the leftovers to `window.onerror`. Nothing threw, nothing is
 * broken, and the page has already settled by the time anyone could read it. Browsers emit it
 * with no stack and no `error` object — only a message — which is why it arrives in Sentry as
 * an untraceable mystery that cannot be grouped with anything meaningful.
 *
 * It is also the single loudest thing in our Sentry: 59 events across two issue groups
 * (JAVASCRIPT-A, JAVASCRIPT-9) from two users, crowding out real crashes. Any page with a
 * resizable panel and a chart or editor in it produces these; ours have several.
 *
 * This drops it from error REPORTING only. If we ever want to know how often layout thrashes,
 * that is a performance metric, not an exception — measuring it as one would still be wrong.
 *
 * Pure, so it is unit-testable — see resizeObserverNoise.test.ts.
 */

/**
 * True when `reason` is the browser's ResizeObserver loop notice.
 *
 * Matched on the message because that is all the browser provides, and it differs per engine:
 *  - Chromium: "ResizeObserver loop completed with undelivered notifications."
 *  - Firefox / older Chromium: "ResizeObserver loop limit exceeded"
 *
 * Accepts the shapes it actually arrives in: a bare string from `window.onerror`'s message, an
 * ErrorEvent's message, or an Error whose message carries it (our own `toError` wraps the
 * string before reporting, which is how JAVASCRIPT-9 came to be a separate group from
 * JAVASCRIPT-A — the same notice, grouped twice).
 */
export function isResizeObserverLoopNotice(reason: unknown): boolean {
  const text =
    typeof reason === "string"
      ? reason
      : reason &&
          typeof reason === "object" &&
          typeof (reason as { message?: unknown }).message === "string"
        ? (reason as { message: string }).message
        : "";
  if (text === "") return false;
  const message = text.toLowerCase();
  if (!message.includes("resizeobserver")) return false;
  return (
    message.includes("loop completed with undelivered") || message.includes("loop limit exceeded")
  );
}
